import { Form, message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCart } from 'stores/cartPOSContext';
import { Cart, CartStatus, CustomerInput } from 'types/common';
import Customer, { CustomerType } from 'models/Customer';
import { SaleOrderPayload, SaleOrderStatusCode } from 'models/SaleOrder';
import {
  useCheckUniqueMutation,
  useGetStatusQuery,
  useGenerateCodeMutation,
  useGetCustomersQuery,
  useCreateSaleOrderMutation,
  useGetAllWarehousesQuery,
  useLazyGetDebtsQuery,
  useGetAllTransactionMethodsQuery,
  useCreateAttachmentMutation,
  useGetAllAttachmentTypesQuery,
} from 'services';
import { FileData, TableEnum } from 'types';
import { Debt, PaymentType } from 'models';
import { TransactionMethodCode } from 'models/TransactionMethod';
import { showAlertNotification } from 'components/common';
import { isUUID } from 'utils/formRules';
import { ETransType } from 'models/Transaction';
import { useUpdateDraftSaleOrderMutation } from 'services/sale.service';
import { useNavigate } from 'react-router-dom';

export const useOrderInfoOperations = () => {
  const { getActiveCart, updateCustomer, updateCart, updateCartStatus } = useCart();
  const activeCart = getActiveCart();
  const [isOpenDebtModal, setIsOpenDebtModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [generateCode, { data: generatedCode, isLoading: isGeneratingCode }] = useGenerateCodeMutation();
  const [getDebts] = useLazyGetDebtsQuery();
  const [createSaleOrder, { isLoading: isCreatingSaleOrder }] = useCreateSaleOrderMutation();
  const [updateDraftSaleOrder] = useUpdateDraftSaleOrderMutation();
  const [checkUnique, { isLoading: isCheckingUnique }] = useCheckUniqueMutation();
  const [customerDebts, setCustomerDebts] = useState<Debt[]>([]);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [transactionMethod, setTransactionMethod] = useState<TransactionMethodCode>('CASH');
  const [createAttachment] = useCreateAttachmentMutation();
  const [transactionAttachments, setTransactionAttachments] = useState<FileData[]>([]);
  const [prefixCode, setPrefixCode] = useState<string>('');
  const navigate = useNavigate();
  const [idDebt, setIdDebt] = useState<string>('');
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  const { data: attachmentTypes } = useGetAllAttachmentTypesQuery({
    query: {
      size: 'unlimited',
    },
  });
  const {
    data: customersResponse,
    isLoading,
    refetch: refetchCustomers,
  } = useGetCustomersQuery({
    query: {
      size: 'unlimited',
      include: 'baseUser',
      // includeRef: 'debt',
    },
  });
  const { data: transactionMethodsData } = useGetAllTransactionMethodsQuery({
    query: {
      size: 'unlimited',
    },
  });
  const { data: statusesResponse } = useGetStatusQuery({
    query: {
      filter: 'code:in:["so-paid","so-debt","so-draft"]',
    },
  });
  const { data: warehouseResponse } = useGetAllWarehousesQuery({
    query: {
      size: 'unlimited',
    },
  });
  const customers = useMemo(() => customersResponse?.data?.rows || [], [customersResponse]);

  const customerOptions = useMemo(() => {
    return customers.map((customer) => ({
      label: customer.baseUser?.name || 'N/A',
      value: customer.id,
      customer: customer,
    }));
  }, [customers]);
  const memberDiscount = useMemo(() => {
    return selectedCustomer?.memberRank?.discount || 0;
  }, [selectedCustomer]);
  const handleValidateOrder = useCallback(async () => {
    if (!activeCart) return false;

    // Kiểm tra giỏ hàng trống
    if (activeCart.items.length === 0) {
      showAlertNotification({
        type: 'error',
        title: 'Lỗi',
        message: 'Vui lòng thêm sản phẩm vào giỏ hàng',
      });
      return false;
    }

    // Kiểm tra sản phẩm không hợp lệ
    const invalidItems = activeCart?.items.filter((item) => item.quantity <= 0 || !item.unitPrice);
    if (invalidItems?.length !== 0) {
      showAlertNotification({
        type: 'warning',
        title: 'Thông báo',
        message: `Vui lòng kiểm tra lại số lượng của các sản phẩm ${invalidItems?.map((item) => `\n- ${item.productVersion.name} ${item.productVersion.color} ${item.productVersion.dimension}`).join('')}`,
      });
      return false;
    }

    // Kiểm tra mã đơn hàng đã tồn tại
    try {
      const response = await checkUnique({
        table: TableEnum.SaleOrders,
        field: 'code',
        value: activeCart.code || '',
      }).unwrap();

      if (response?.data?.exists) {
        form.setFields([
          {
            name: 'code',
            errors: [`Mã đơn hàng "${activeCart.code}" đã tồn tại. Vui lòng sử dụng mã khác.`],
          },
        ]);
        showAlertNotification({
          type: 'warning',
          title: 'Thông báo',
          message: `Mã đơn hàng "${activeCart.code}" đã tồn tại. Vui lòng sử dụng mã khác.`,
        });
        return false;
      }

      // Chỉ trả về true khi tất cả điều kiện đều hợp lệ
      return true;
    } catch (error) {
      console.error('Lỗi khi kiểm tra mã đơn hàng:', error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi',
        message: 'Đã xảy ra lỗi khi kiểm tra mã đơn hàng. Vui lòng thử lại sau.',
      });
      return false;
    }
  }, [activeCart, selectedCustomer, transactionMethod, transactionMethodsData, checkUnique]);
  const handleFinishOrder = useCallback(
    async (id?: string, type?: string) => {
      const paymentData = form.getFieldsValue();
      if (activeCart) {
        try {
          // Get statuses from the query
          const statuses = statusesResponse?.data || [];
          const paidStatusId = statuses.find((status) => status.code === SaleOrderStatusCode.PAID)?.id;
          const debtStatusId = statuses.find((status) => status.code === SaleOrderStatusCode.DEBT)?.id;
          const draftStatusId = statuses.find((status) => status.code === SaleOrderStatusCode.DRAFT)?.id;
          if (!paidStatusId && !debtStatusId && !draftStatusId) {
            message.error('Không tìm thấy trạng thái thanh toán hợp lệ');
            return;
          }

          console.log(paymentData);
          // Determine if order is fully paid or has debt
          const hasDebt = paymentData.paymentType !== PaymentType.FULL_PAYMENT;
          const statusId = paymentData.status === 'draft' ? draftStatusId! : hasDebt ? debtStatusId! : paidStatusId!;

          // Prepare sale order items
          const saleOrderItems = activeCart.items.map((cartItem) => {
            const { productVersion, quantity, attribute, saleItemDimesions } = cartItem;

            // Get sale dimensions from the product version's category
            const saleDimensions = productVersion.category?.saleDimensions || '';

            return {
              productVersionId: productVersion.id!,
              saleDimensions,
              quantity,
              costPrice: Number(productVersion.costPrice) || 0,
              salePrice: Number(cartItem.unitPrice) || 0,
              name: cartItem.name || productVersion.name || '',
              category: productVersion.category?.name || '',
              brand: productVersion.brand?.name || '',
              productDimension: productVersion.dimension || '',
              sizeSpec: productVersion.sizeSpec || '',
              color: productVersion.color || '',
              attributeName: attribute?.attribute?.name || '',
              attributePrice: attribute?.salePrice || 0,
              unit: cartItem.unit?.name || '',
              unitId: cartItem.unit?.id || '',
              unitConvertValue: cartItem.unitConvertValue || 1,
              saleItemDimensions:
                saleItemDimesions?.map((dim) => ({
                  dimensions: dim.dimensions,
                  quantity: dim.quantity,
                  order: dim.order,
                })) || [],
            };
          });

          const customer = refactorCustomer(activeCart.customer);

          const dateObj = paymentData?.createdAt ? new Date(paymentData.createdAt) : new Date();

          const createdAt = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 19); // YYYY-MM-DDTHH:mm:ss

          // Create the sale order payload
          const saleOrderPayload: SaleOrderPayload = {
            code: activeCart.code,
            description: paymentData.note || '',
            statusId,
            customerId: customer?.id || null,
            customerName: customer?.name || '',
            customerEmail: customer?.email || undefined,
            customerPhone: customer?.phone || '',
            customerAddress: customer?.address || '',
            warehouseId: warehouseResponse?.data?.rows[0]?.id || '',
            memberDiscount: Number(memberDiscount) || 0,
            discount: Number(paymentData.discountValue) || 0,
            discountUnit: paymentData.discountType, // or PERCENT based on your logic
            total:
              paymentData.paymentType === PaymentType.FULL_PAYMENT
                ? Number(paymentData.prepaidAmount || 0)
                : Number(paymentData.remainingAmount || 0) +
                  Number(paymentData.prepaidAmount || 0) +
                  Number(paymentData.extraFee || 0),
            paymentType:
              Number(paymentData.remainingAmount || 0) === 0 ? PaymentType.FULL_PAYMENT : paymentData.paymentType,
            scheduleDate: paymentData.scheduleDate,
            deliveryDate: paymentData.deliveryDate,
            vat: Number(paymentData.vatValue) || 0,
            debtDeadline: paymentData.paymentDueDate ? new Date(paymentData.paymentDueDate).toISOString() : undefined,
            saleOrderItems,
            createdAt, // Add createdAt here
            transactions: [
              {
                transactionMethodId: transactionMethodsData?.data.rows.filter(
                  (method) => method.code === transactionMethod,
                )?.[0].id,
                transactionAmount: !hasDebt
                  ? paymentData.paymentType === PaymentType.FULL_PAYMENT
                    ? Number(paymentData.prepaidAmount || 0)
                    : Number(paymentData.remainingAmount || 0) + Number(paymentData.prepaidAmount || 0)
                  : Number(paymentData.prepaidAmount || 0),
                transactionAttachments: transactionAttachments.map((file) => ({
                  attachmentId: file.id || '',
                  description: 'transaction attachment sale order',
                })),
                ownerType: 'CUSTOMER',
                type: ETransType.Deposit,
              },
              ...(paymentData.extraFee && Number(paymentData.extraFee) > 0
                ? [
                    {
                      transactionMethodId: transactionMethodsData?.data.rows.filter(
                        (method) => method.code === transactionMethod,
                      )?.[0].id,
                      transactionAmount: Number(paymentData.extraFee || 0),
                      ownerType: 'CUSTOMER' as const,
                      type: ETransType.OS_EXTRA_FEE,
                    },
                  ]
                : []),
            ],
          };

          if (hasDebt) {
            saleOrderPayload.deposit = Number(paymentData.prepaidAmount);
            saleOrderPayload.depositDate = new Date().toISOString();
          }
          let result;
          console.log(type);
          if (type === 'edit' && id) {
            result = await updateDraftSaleOrder({
              id,
              data: saleOrderPayload,
            }).unwrap();
          } else {
            result = await createSaleOrder(saleOrderPayload).unwrap();
          }

          // If successful,  show success message
          if (result.success) {
            showAlertNotification({
              type: 'success',
              title: 'Tạo đơn hàng thành công',
              message:
                'Đơn hàng đã được tạo thành công' +
                (paymentData.paymentType !== 'full-payment' ? ' với công nợ được tạo.' : ''),
            });
            // Nếu đang ở trang edit, cập nhật lại URL với id mới nếu có
            if (result.data?.id) {
              // Xóa params bằng navigate
              navigate(
                {
                  pathname: window.location.pathname, // giữ nguyên trang hiện tại
                  search: '', // clear toàn bộ query
                },
                { replace: true },
              ); // replaceState tương đương
            }
            updateCartStatus(activeCart.id, CartStatus.COMPLETED);
            updateCart(activeCart.id, { saleOrderId: result.data.id });
            refetchCustomers();
          }
        } catch (error) {
          console.log(error);
          showAlertNotification({
            type: 'error',
            title: 'Lỗi khi tạo đơn hàng',
            message: 'Đã xảy ra lỗi khi tạo đơn hàng. Vui lòng thử lại sau.',
          });
        }
      }
    },
    [
      activeCart,
      statusesResponse,
      createSaleOrder,
      memberDiscount,
      updateCartStatus,
      transactionMethod,
      transactionAttachments,
    ],
  );

  const refactorCustomer = (customer?: CustomerInput | null) => {
    if (!customer) return null;

    const safeField = (val?: string | null): string | null => {
      if (!val) return null;
      return isUUID(val) ? null : val;
    };

    return {
      id: customer.id ?? null,
      name: safeField(customer.name),
      email: safeField(customer.email),
      phone: safeField(customer.phone),
      address: safeField(customer.address),
    };
  };

  const [form] = Form.useForm();
  useEffect(() => {
    console.log('activeCart', activeCart?.id);
    if (activeCart) {
      // Set form values based on active cart
      form.setFieldsValue({
        name: activeCart.customer?.name || '',
        phone: activeCart.customer?.phone || '',
        email: activeCart.customer?.email || '',
        address: activeCart.customer?.address || '',
        code: activeCart.code || '',
        note: activeCart.note || '',
        createdAt: activeCart.paymentData?.createdAt ? new Date(activeCart.paymentData.createdAt) : new Date(),
      });

      // If cart has a customer ID, select that customer
      if (activeCart.customer?.id) {
        // console.log('Active cart has customer ID:', activeCart.customer.id);
        // handleSelectCustomer(activeCart.customer.id);
      } else {
        setSelectedCustomer(null);
        setCustomerDebts([]);
      }

      // Reset transaction method if stored in cart
      if (activeCart.transactionMethod) {
        setTransactionMethod(activeCart.transactionMethod as TransactionMethodCode);
      } else {
        setTransactionMethod('CASH');
      }

      // Reset attachments
      setTransactionAttachments(activeCart.transactionAttachments || []);
    }
  }, [activeCart?.id]);
  useEffect(() => {
    if (!activeCart?.code) {
      generateOrderCode();
    } else {
      setPrefixCode((form.getFieldValue('code') || '').substring(0, 6));
    }
  }, []);

  useEffect(() => {
    if (generatedCode && activeCart) {
      form.setFieldsValue({ code: generatedCode });
      updateCart(activeCart.id, { code: generatedCode });
    }
  }, [generatedCode, form]);

  const handleBack = useCallback(() => {
    updateCartStatus(activeCart?.id || '', CartStatus.PRODUCT_SELECTION);
  }, [activeCart?.id, updateCartStatus]);

  const handleCustomerInfoChange = (changedValues: Partial<Cart['customer']>) => {
    updateCustomer({
      ...activeCart?.customer,
      ...changedValues,
    });
  };

  const handleOpenDebtModal = () => {
    setIsOpenDebtModal(true);
  };

  const handleSelectCustomer = async (value: string | number) => {
    const selectedCust = customers.find((customer) => customer.id === value);

    if (selectedCust) {
      const res = await getDebts({
        query: {
          filter: `phoneNumber:eq:${selectedCust.baseUser?.phoneNumber}&&debtType:eq:CUSTOMER`,
          size: 'unlimited',
          include: 'status',
        },
      });
      console.log('Selected customer ID:', value);
      setCustomerDebts(res.data?.data.rows || []);
      setSelectedCustomer(selectedCust);

      const masterAddress =
        selectedCust.type === CustomerType.PERSONAL
          ? selectedCust.baseUser?.address
          : selectedCust.companyAddress || selectedCust.baseUser?.address;
      const preservedAddress =
        activeCart?.customer?.id === selectedCust.id && activeCart.customer?.address
          ? activeCart.customer.address
          : undefined;
      const resolvedAddress = preservedAddress ?? masterAddress ?? '';
      form.setFieldsValue({
        name: selectedCust.baseUser?.name || '',
        phone: selectedCust.baseUser?.phoneNumber || '',
        email: selectedCust.baseUser?.email || '',
        address: resolvedAddress,
      });
      updateCustomer({
        id: selectedCust.id,
        name: selectedCust.baseUser?.name || '',
        phone: selectedCust.baseUser?.phoneNumber || '',
        email: selectedCust.baseUser?.email || '',
        address: resolvedAddress,
      });
    } else {
      setSelectedCustomer(null);
      updateCustomer({
        ...activeCart?.customer,
        id: undefined,
      });
    }
  };

  const generateOrderCode = useCallback(async () => {
    try {
      const res = await generateCode({ code: 'SO' }).unwrap();
      if (res) {
        form.setFieldValue('code', res);
        setPrefixCode(res.substring(0, 6));
      } else {
        console.error('generateOrderCode failed, no data returned.');
      }
    } catch (err) {
      console.error('generateOrderCode failed:', err);
    }
  }, [generateCode, form]);

  const totalAmount = useMemo(() => {
    if (!activeCart?.items || activeCart.items.length === 0) return 0;
    return activeCart.items.reduce((sum, item) => sum + item.totalPrice, 0);
  }, [activeCart?.items]);

  const memberPoints = useMemo(() => {
    return selectedCustomer?.points ? parseFloat(String(selectedCustomer.points)) : 0;
  }, [selectedCustomer]);

  const memberRank = useMemo(() => {
    return selectedCustomer?.memberRank?.name || 'Member Ship';
  }, [selectedCustomer]);

  const memberRankColor = useMemo(() => {
    return selectedCustomer?.memberRank?.color || '#FF823E';
  }, [selectedCustomer]);

  const hasDebts = useMemo(() => {
    console.log('customerDebts', customerDebts);
    return customerDebts.length > 0;
  }, [selectedCustomer, customerDebts]);
  useEffect(() => {
    if (activeCart?.customer?.id && customers.length > 0) {
      console.log('Customers list updated, checking active cart customer ID...', activeCart?.customer?.id, customers);
      handleSelectCustomer(activeCart.customer.id);
    }
  }, [customers]);
  const handleUpload = useCallback(
    async (files: File[]): Promise<void> => {
      try {
        for (const file of files) {
          const attachment = await createAttachment({
            name: 'Transaction Attachment ' + new Date().toISOString(),
            filePath: file.name,
            attachmentTypeId: attachmentTypes?.data?.rows.filter((type) => type.code === 'transaction')[0]?.id || '',
          }).unwrap();
          setTransactionAttachments((prev) => [
            ...prev,
            {
              id: attachment.data.id,
              filePath: attachment.data.filePath,
              order: prev.length + 1,
              name: file.name,
            },
          ]);
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        showAlertNotification({
          type: 'error',
          title: 'Lỗi tải lên tệp',
          message: 'Đã xảy ra lỗi khi tải lên tệp. Vui lòng thử lại.',
        });
      }
    },
    [createAttachment, attachmentTypes],
  );
  const handleTransactionMethodChange = useCallback(
    (method: TransactionMethodCode) => {
      setTransactionMethod(method);
      if (activeCart) {
        updateCart(activeCart.id, { transactionMethod: method });
      }
    },
    [activeCart, updateCart],
  );
  // Refresh customer debts (used after paying in detail modal)
  const refreshCustomerDebts = useCallback(async () => {
    try {
      const phone = selectedCustomer?.baseUser?.phoneNumber || activeCart?.customer?.phone;
      if (!phone) return;
      const res = await getDebts({
        query: {
          filter: `phoneNumber:eq:${phone}&&debtType:eq:CUSTOMER`,
          size: 'unlimited',
          include: 'status',
        },
      });
      setCustomerDebts(res.data?.data.rows || []);
    } catch (e) {
      console.error('Failed to refresh customer debts:', e);
    }
  }, [getDebts, selectedCustomer?.baseUser?.phoneNumber, activeCart?.customer?.phone]);
  const handleOpenDebtDetailModal = (id: string) => {
    setIdDebt(id);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    // After closing (likely after payment action), refresh debts so QuickView updates
    refreshCustomerDebts();
  };
  return {
    // Form
    form,

    // State
    activeCart,
    selectedCustomer,
    setSelectedCustomer,
    customerDebts,
    isOpenDebtModal,
    setIsOpenDebtModal,
    isFileUploadModalOpen,
    setIsFileUploadModalOpen,
    transactionMethod,
    setTransactionMethod,
    handleValidateOrder,
    // Data
    customers,

    customerOptions,

    // Computed values
    memberDiscount,
    totalAmount,
    memberPoints,
    memberRank,
    memberRankColor,
    hasDebts,
    prefixCode,

    // Event handlers
    handleFinishOrder,
    handleBack,
    handleCustomerInfoChange,
    handleOpenDebtModal,
    handleSelectCustomer,
    generateOrderCode,
    handleUpload,
    handleTransactionMethodChange,
    refreshCustomerDebts,
    handleOpenDebtDetailModal,
    handleCloseDetailModal,
    // Loading states
    isGeneratingCode,
    isCreatingSaleOrder,
    isCheckingUnique,
    isLoading,
    isDetailModalOpen,
    idDebt,
  };
};
